import {
  CLOSE,
  RECONNECT_GIVE_UP_MS,
  reconnectDelayMs,
  type ErrorCode,
  type EventFrame,
  type Method,
  type RequestData,
  type ResponseData,
} from '@calling/shared';

export class RpcError extends Error {
  constructor(
    readonly code: ErrorCode | 'timeout' | 'disconnected',
    message: string,
  ) {
    super(message);
  }
}

export type SignalingStatus = 'connecting' | 'open' | 'reconnecting' | 'lost' | 'closed';

export interface SignalingOptions {
  /** Builds the socket URL from query parameters. */
  url: (query: Record<string, string>) => string;
  /** The ticket from the join request that started this session. */
  initialTicket: string;
  /** Gets a fresh join ticket when the session cannot be resumed. A refusal ends the session. */
  getTicket: () => Promise<string>;
  onEvent: (frame: EventFrame) => void;
  onStatus: (status: SignalingStatus) => void;
  /** The ticket request was refused (locked, blocked, ended…). */
  onFatal: (error: unknown) => void;
  createSocket?: (url: string) => WebSocket;
  pingIntervalMs?: number;
  requestTimeoutMs?: number;
  connectTimeoutMs?: number;
}

interface Pending {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * The signaling connection: request/response over one WebSocket, plus the
 * reconnect behaviour from E6-S6. When the socket drops it reconnects with
 * exponential backoff (1, 2, 4, 8, then 10 s), resuming the same session so
 * the participant keeps their place; after 60 s without success it reports
 * the connection as lost.
 */
export class Signaling {
  private ws: WebSocket | null = null;
  private nextId = 1;
  private readonly pending = new Map<number, Pending>();
  private resume: string | null = null;
  private attempt = 0;
  private droppedAt: number | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private lostTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private finished = false;
  private ticket: string | null;
  status: SignalingStatus = 'connecting';

  constructor(private readonly opts: SignalingOptions) {
    this.ticket = opts.initialTicket;
  }

  start(): void {
    void this.open();
  }

  private setStatus(status: SignalingStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.opts.onStatus(status);
  }

  private async open(): Promise<void> {
    if (this.finished) return;
    let query: Record<string, string>;
    if (this.resume) {
      query = { resume: this.resume };
    } else if (this.ticket) {
      // A ticket is good for one connection attempt.
      query = { ticket: this.ticket };
      this.ticket = null;
    } else {
      try {
        query = { ticket: await this.opts.getTicket() };
      } catch (err) {
        if (this.finished) return;
        // A refusal is final; a network failure is just another failed attempt.
        if (isRefusal(err)) {
          // Report first: finishing changes the status, and the owner must
          // already know why by then.
          this.opts.onFatal(err);
          this.finish();
        } else {
          this.scheduleRetry();
        }
        return;
      }
    }
    if (this.finished) return;
    const ws = (this.opts.createSocket ?? ((u) => new WebSocket(u)))(this.opts.url(query));
    this.ws = ws;
    // On a dead network a connection attempt can hang for minutes without
    // failing. Give each one a few seconds, then move on to the next.
    this.connectTimer = setTimeout(() => {
      if (this.ws !== ws || ws.readyState === 1) return;
      this.abandon(ws);
      this.scheduleRetry();
    }, this.opts.connectTimeoutMs ?? 8000);
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.clearConnectTimer();
      if (this.lostTimer) clearTimeout(this.lostTimer);
      this.lostTimer = null;
      this.attempt = 0;
      this.droppedAt = null;
      this.setStatus('open');
      this.startPing();
    };
    ws.onmessage = (message) => {
      if (this.ws !== ws) return;
      let frame: unknown;
      try {
        frame = JSON.parse(String(message.data));
      } catch {
        return;
      }
      this.onFrame(frame as Record<string, unknown>);
    };
    ws.onclose = (event) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearConnectTimer();
      this.stopPing();
      this.failPending(new RpcError('disconnected', 'The connection was interrupted.'));
      if (this.finished) return;
      if (event.code === CLOSE.replaced) return this.finish();
      // The server no longer knows this session: ask to join again instead.
      if (event.code === CLOSE.gone || event.code === CLOSE.badTicket) this.resume = null;
      this.scheduleRetry();
    };
    ws.onerror = () => {
      // A close event always follows; reconnecting is handled there.
    };
  }

  private onFrame(frame: Record<string, unknown>): void {
    if (typeof frame.event === 'string') {
      const event = frame as unknown as EventFrame;
      if (event.event === 'hello') this.resume = (event as EventFrame<'hello'>).data.resume;
      this.opts.onEvent(event);
      return;
    }
    if (typeof frame.id !== 'number') return;
    const pending = this.pending.get(frame.id);
    if (!pending) return;
    this.pending.delete(frame.id);
    clearTimeout(pending.timer);
    if (frame.ok === true) pending.resolve(frame.data);
    else {
      const error = (frame.error ?? {}) as { code?: ErrorCode; message?: string };
      pending.reject(new RpcError(error.code ?? 'internal', error.message ?? 'Request failed.'));
    }
  }

  private clearConnectTimer(): void {
    if (this.connectTimer) clearTimeout(this.connectTimer);
    this.connectTimer = null;
  }

  /** Stops caring about a socket: its late events are ignored. */
  private abandon(ws: WebSocket): void {
    if (this.ws === ws) this.ws = null;
    this.clearConnectTimer();
    ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
    try {
      ws.close();
    } catch {
      /* already closed */
    }
  }

  private giveUp(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    if (this.lostTimer) clearTimeout(this.lostTimer);
    this.lostTimer = null;
    if (this.ws) this.abandon(this.ws);
    this.setStatus('lost');
  }

  private scheduleRetry(): void {
    if (this.finished || this.retryTimer) return;
    if (this.droppedAt === null) {
      this.droppedAt = Date.now();
      // The 60-second limit holds whatever the attempts are doing, including
      // hanging without ever reporting a failure.
      this.lostTimer = setTimeout(() => {
        this.lostTimer = null;
        if (!this.finished && this.status !== 'open') this.giveUp();
      }, RECONNECT_GIVE_UP_MS);
    }
    if (Date.now() - this.droppedAt >= RECONNECT_GIVE_UP_MS) return this.giveUp();
    this.setStatus('reconnecting');
    const delay = reconnectDelayMs(this.attempt++);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.open();
    }, delay);
  }

  /** Tries again at once, e.g. when the device reports it is back online. */
  retryNow(): void {
    if (this.finished || this.ws) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    if (this.status === 'lost') {
      this.droppedAt = null;
      this.attempt = 0;
      this.setStatus('reconnecting');
    }
    void this.open();
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      // A socket can look open long after the network is gone; an unanswered
      // ping is how we find out.
      this.request('ping', {}, this.opts.pingIntervalMs ?? 5000).catch(() => {
        this.ws?.close();
      });
    }, this.opts.pingIntervalMs ?? 5000);
  }

  private stopPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private failPending(error: Error): void {
    for (const p of this.pending.values()) {
      clearTimeout(p.timer);
      p.reject(error);
    }
    this.pending.clear();
  }

  get connected(): boolean {
    return this.ws !== null && this.ws.readyState === 1;
  }

  request<M extends Method>(method: M, data: RequestData<M>, timeoutMs = this.opts.requestTimeoutMs ?? 10_000): Promise<ResponseData<M>> {
    const ws = this.ws;
    if (!ws || ws.readyState !== 1) return Promise.reject(new RpcError('disconnected', 'Not connected.'));
    const id = this.nextId++;
    return new Promise<ResponseData<M>>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new RpcError('timeout', 'The server did not respond.'));
      }, timeoutMs);
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject, timer });
      ws.send(JSON.stringify({ id, method, data }));
    });
  }

  private finish(): void {
    this.finished = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    if (this.lostTimer) clearTimeout(this.lostTimer);
    this.lostTimer = null;
    this.clearConnectTimer();
    this.stopPing();
    this.setStatus('closed');
  }

  /** Stops for good: no further reconnect attempts. */
  close(): void {
    this.finish();
    const ws = this.ws;
    this.ws = null;
    this.failPending(new RpcError('disconnected', 'Closed.'));
    ws?.close();
  }
}

/** An HTTP 4xx answer from the join endpoint, as opposed to not reaching it. */
function isRefusal(err: unknown): boolean {
  const status = (err as { status?: number }).status;
  const code = (err as { code?: string }).code;
  // Being rate limited is temporary; a full waiting room is an answer.
  if (status === 429) return code === 'full';
  return typeof status === 'number' && status >= 400 && status < 500;
}
